import { CHARSET_NUMERIC, getDefaultGlyphAtlas, imageChannels, imageStride, } from 'maplestory-glyph-decoder';
import { RAW_OWL_BILLION_OVERLAP_GLYPHS, } from './generated/billion-overlap-glyphs.js';
import { OWL_INTEGER_DIGIT_WIDTH, OWL_INTEGER_GLYPH_HEIGHT, owlIntegerDigitSlotLeft, owlLeftAlignedSlotCapacity, owlRightAlignedSlotCapacity, } from './integer-slot-geometry.js';
const BLANK_DIGIT_MASK = '\0'.repeat(OWL_INTEGER_DIGIT_WIDTH);
const compiledAtlasCache = new WeakMap();
let compiledMixedDigits;
const compiledOverlapDigits = new Map();
/** Compile a 7x12 binary ink shape into seven UTF-16 code units.
 *
 * Each code unit stores one 12-bit vertical column. Generated glyph weights use
 * zero for background and non-zero for glyph ink.
 */
function compileGlyphInk(weights, width, visualWidth) {
    let out = '';
    for (let x = 0; x < width; x++) {
        let column = 0;
        for (let y = 0; y < OWL_INTEGER_GLYPH_HEIGHT; y++) {
            if ((weights[y * visualWidth + x] ?? 0) !== 0)
                column |= 1 << y;
        }
        out += String.fromCharCode(column);
    }
    return out;
}
function validateIntegerDigit(atlas, digit) {
    const glyph = atlas.byChar.get(digit);
    if (!glyph)
        throw new Error(`integer atlas is missing digit ${digit}`);
    if (glyph.width !== OWL_INTEGER_DIGIT_WIDTH || glyph.visualWidth < OWL_INTEGER_DIGIT_WIDTH) {
        throw new Error(`integer digit ${digit} must have logical width ${OWL_INTEGER_DIGIT_WIDTH}`);
    }
    return glyph;
}
function compileIntegerDigits(atlas) {
    const cached = compiledAtlasCache.get(atlas);
    if (cached)
        return cached;
    const byMask = new Map();
    for (const digit of CHARSET_NUMERIC) {
        const glyph = validateIntegerDigit(atlas, digit);
        const mask = compileGlyphInk(glyph.weights, glyph.width, glyph.visualWidth);
        const existing = byMask.get(mask);
        if (existing !== undefined) {
            throw new Error(`integer digits ${existing} and ${digit} have the same binary ink shape`);
        }
        byMask.set(mask, digit);
    }
    const compiled = { byMask };
    compiledAtlasCache.set(atlas, compiled);
    return compiled;
}
/** Compile the 10 RGB and 10 BGR digit silhouettes into one exact lookup
 * table.
 *
 * All 20 masks are expected to be distinct. A collision is treated as an atlas
 * generation error even when both templates represent the same digit, because
 * the runtime decoder relies on one unambiguous mask-to-digit lookup.
 */
function compileMixedIntegerDigits() {
    if (compiledMixedDigits)
        return compiledMixedDigits;
    const variants = ['cleartype-rgb', 'cleartype-bgr'];
    const byMask = new Map();
    const sourceByMask = new Map();
    for (const variant of variants) {
        const atlas = getDefaultGlyphAtlas({ variant, charset: CHARSET_NUMERIC });
        for (const digit of CHARSET_NUMERIC) {
            const glyph = validateIntegerDigit(atlas, digit);
            const mask = compileGlyphInk(glyph.weights, glyph.width, glyph.visualWidth);
            const source = `${variant}:${digit}`;
            const existingSource = sourceByMask.get(mask);
            if (existingSource !== undefined) {
                throw new Error(`integer glyphs ${existingSource} and ${source} have the same binary ink shape`);
            }
            sourceByMask.set(mask, source);
            byMask.set(mask, digit);
        }
    }
    if (byMask.size !== variants.length * CHARSET_NUMERIC.length) {
        throw new Error(`expected 20 distinct RGB/BGR integer glyph masks, got ${byMask.size}`);
    }
    const compiled = { byMask };
    compiledMixedDigits = compiled;
    return compiled;
}
function compileOverlapDigits(field, variant) {
    const variantKey = variant === 'cleartype-rgb' || variant === 'cleartype-bgr' ? variant : 'auto';
    const cacheKey = `${field}:${variantKey}`;
    const cached = compiledOverlapDigits.get(cacheKey);
    if (cached)
        return cached;
    const byMask = new Map();
    for (const glyph of RAW_OWL_BILLION_OVERLAP_GLYPHS) {
        if (glyph.field !== field)
            continue;
        if (variantKey !== 'auto' && glyph.variant !== variantKey)
            continue;
        const existing = byMask.get(glyph.mask);
        if (existing !== undefined && existing !== glyph.digit) {
            throw new Error(`${field} billion-overlap glyph mask maps to both ${existing} and ${glyph.digit}`);
        }
        byMask.set(glyph.mask, glyph.digit);
    }
    const expected = variantKey === 'auto' ? 6 : 3;
    if (byMask.size !== expected) {
        throw new Error(`expected ${expected} distinct ${field} billion-overlap masks for ${variantKey}, got ${byMask.size}`);
    }
    compiledOverlapDigits.set(cacheKey, byMask);
    return byMask;
}
/** Compile a screenshot slot with white as background and any non-white RGB
 * pixel as ink. The result has the same 12-bit-column string representation as
 * the precompiled glyph masks.
 */
function compileSlotInk(image, x0) {
    const channels = imageChannels(image);
    const stride = imageStride(image);
    let out = '';
    for (let x = 0; x < OWL_INTEGER_DIGIT_WIDTH; x++) {
        let column = 0;
        for (let y = 0; y < OWL_INTEGER_GLYPH_HEIGHT; y++) {
            const offset = y * stride + (x0 + x) * channels;
            const r = image.data[offset] ?? 255;
            const g = image.data[offset + 1] ?? 255;
            const b = image.data[offset + 2] ?? 255;
            if (r !== 255 || g !== 255 || b !== 255)
                column |= 1 << y;
        }
        out += String.fromCharCode(column);
    }
    return out;
}
function formatIntegerDigits(digits) {
    return digits.replace(/\B(?=(?:\d{3})+(?!\d))/gu, ',');
}
function decodeRightAligned(image, compiled, options) {
    if (image.height !== OWL_INTEGER_GLYPH_HEIGHT)
        return '';
    const capacity = owlRightAlignedSlotCapacity(image.width);
    if (capacity === 0)
        return '';
    const reversedDigits = [];
    const overlapDigits = options.billionOverlapField
        ? compileOverlapDigits(options.billionOverlapField, options.atlasVariant)
        : undefined;
    const overlapSlotIndex = options.billionOverlapField === 'bundle' ? 0 : capacity - 1;
    for (let index = 0; index < capacity; index++) {
        const x = owlIntegerDigitSlotLeft(image.width, index);
        const slotMask = compileSlotInk(image, x);
        if (slotMask === BLANK_DIGIT_MASK)
            break;
        if (overlapDigits !== undefined && index === overlapSlotIndex) {
            const digit = overlapDigits.get(slotMask);
            if (digit === undefined)
                return '';
            reversedDigits.push(digit);
            continue;
        }
        if (options.knownLeadingDigit !== undefined && index === capacity - 1) {
            reversedDigits.push(options.knownLeadingDigit);
            continue;
        }
        const digit = compiled.byMask.get(slotMask);
        if (digit === undefined)
            return '';
        reversedDigits.push(digit);
    }
    if (reversedDigits.length === 0)
        return '';
    // A supplied leading digit identifies the most-significant physical slot,
    // so every slot through that prefix must be occupied.
    if ((options.knownLeadingDigit !== undefined || options.billionOverlapField === 'price') && reversedDigits.length !== capacity)
        return '';
    return formatIntegerDigits(reversedDigits.reverse().join(''));
}
function decodeLeftAligned(image, compiled) {
    if (image.height !== OWL_INTEGER_GLYPH_HEIGHT)
        return '';
    const capacity = owlLeftAlignedSlotCapacity(image.width);
    if (capacity === 0)
        return '';
    const digits = [];
    for (let index = 0; index < capacity; index++) {
        const x = index * OWL_INTEGER_DIGIT_WIDTH;
        const slotMask = compileSlotInk(image, x);
        if (slotMask === BLANK_DIGIT_MASK)
            break;
        const digit = compiled.byMask.get(slotMask);
        if (digit === undefined)
            return '';
        digits.push(digit);
    }
    return digits.join('');
}
function compileDefaultIntegerDigits(options) {
    // An explicitly supplied atlas is authoritative and is never combined with a
    // default complementary atlas.
    if (options.atlas)
        return compileIntegerDigits(options.atlas);
    if (options.atlasVariant === 'cleartype-rgb' || options.atlasVariant === 'cleartype-bgr') {
        return compileIntegerDigits(getDefaultGlyphAtlas({
            variant: options.atlasVariant,
            charset: CHARSET_NUMERIC,
        }));
    }
    return compileMixedIntegerDigits();
}
/** Decode a right-aligned Owl integer from fixed digit slots.
 *
 * All Owl numeric cells use the same layout as a right substring of
 * `0,000,000,000`: digits occupy fixed 7px slots and every group of three
 * digits reserves a 3px comma separator. Slots are read from right to left
 * until the first blank slot, and commas are reconstructed from the decoded
 * digit count.
 *
 * Digit classification is exact: atlas ink is `weight != 0`, screenshot ink is
 * `pixel != #ffffff`, and the resulting seven-column 12-bit strings must match.
 * A concrete atlas variant restricts matching to that profile's ten digit
 * masks. Calls without a concrete variant retain the combined 20-mask lookup.
 */
export function decodeOwlIntegerSlots(image, options = {}) {
    return decodeRightAligned(image, compileDefaultIntegerDigits(options), options);
}
/** Decode a right-aligned colored Owl integer from fixed digit slots.
 *
 * This named entry point documents the colored-text use case. A concrete atlas
 * variant restricts matching to that profile; otherwise it uses the same
 * collision-checked 20-mask exact lookup as the shared fixed-slot decoder.
 */
export function decodeOwlColoredIntegerSlots(image, options = {}) {
    return decodeRightAligned(image, compileDefaultIntegerDigits(options), options);
}
/** Decode the Owl header row count from contiguous left-aligned 7px slots.
 *
 * Digits start at x=0, contain no comma separators, and are read left to right
 * until the first blank slot. Each slot must exactly match the selected
 * profile, an explicitly supplied custom atlas, or the compatibility RGB/BGR
 * lookup when no concrete profile is supplied.
 */
export function decodeOwlTotalCountSlots(image, options = {}) {
    return decodeLeftAligned(image, compileDefaultIntegerDigits(options));
}
//# sourceMappingURL=integer-slots.js.map